/* @jsx h */
import { computed, defineComponent, h, type PropType } from "vue";
import { format } from "date-fns";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { Loading } from "@app/vue/components/UIComponents";

type GroupBy = "day" | "week" | "month" | "quarter" | "year";

type BreakdownSeries = {
  key: string;
  name: string;
  color: string;
  data: number[];
};

// Periods (rows) x restaurants (columns) breakdown, mirroring the Revenue
// report's payment breakdown table but with restaurants as the dimension. It
// reads the already-filtered series so it always matches the chart above.
export const GroupRevenueBreakdownTable = defineComponent({
  name: "GroupRevenueBreakdownTable",
  props: {
    periods: {
      type: Array as PropType<Date[]>,
      required: true,
    },
    resolution: {
      type: String as PropType<GroupBy>,
      default: "day",
    },
    series: {
      type: Array as PropType<BreakdownSeries[]>,
      required: true,
    },
    totals: {
      type: Array as PropType<number[]>,
      required: true,
    },
    currency: {
      type: String,
      default: "CHF",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useLocalization();

    const money = (value: number) =>
      formatPriceInLocale(value, true, 2, props.currency || "CHF");

    const periodLabels = computed(() =>
      props.periods.map((date) => {
        switch (props.resolution) {
          case "week":
            return format(date, "'Week of' MMM d, yyyy");
          case "month":
            return format(date, "MMM yyyy");
          case "quarter":
            return format(date, "'Q'Q yyyy");
          case "year":
            return format(date, "yyyy");
          default:
            return format(date, "MMM d, yyyy");
        }
      }),
    );

    const periodHeader = computed(() => {
      switch (props.resolution) {
        case "week":
          return t("reports.revenue.period_week");
        case "month":
          return t("reports.revenue.period_month");
        case "quarter":
          return t("reports.revenue.period_quarter");
        case "year":
          return t("reports.revenue.period_year");
        default:
          return t("reports.revenue.period_day");
      }
    });

    const isEmpty = computed(
      () =>
        props.periods.length === 0 ||
        props.series.length === 0 ||
        props.totals.every((value) => value === 0),
    );

    const downloadCSV = () => {
      const headers = [
        periodHeader.value,
        ...props.series.map((serie) => serie.name),
        t("admin.dashboard.total"),
      ];
      const rows = [headers.map((h) => `"${h}"`).join(",")];
      periodLabels.value.forEach((label, index) => {
        const row = [
          `"${label}"`,
          ...props.series.map((serie) => (serie.data[index] ?? 0).toFixed(2)),
          (props.totals[index] ?? 0).toFixed(2),
        ];
        rows.push(row.join(","));
      });
      const blob = new Blob([rows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `revenue-by-restaurant-${props.resolution}-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      link.click();
      URL.revokeObjectURL(url);
    };

    return () => (
      <div class="space-y-4 rounded-2xl border  bg-base-100 p-6 shadow-lg">
        <div class="flex items-center justify-between gap-4 border-b /60 pb-4">
          <div>
            <h3 class="text-lg font-semibold text-accent">
              {t("admin.dashboard.breakdown_title")}
            </h3>
            <p class="text-sm text-base-content/70">
              {t("admin.dashboard.breakdown_subtitle")}
            </p>
          </div>
          <button
            class="btn btn-secondary btn-sm gap-2"
            onClick={downloadCSV}
            disabled={isEmpty.value}
          >
            <span class="material-symbols-outlined text-lg">download</span>
            {t("reports.revenue.download_csv")}
          </button>
        </div>

        <div class="relative">
          {props.loading ? (
            <div class="flex min-h-32 items-center justify-center bg-base-200/60 p-4">
              <Loading />
            </div>
          ) : isEmpty.value ? (
            <div class="p-6 text-center text-sm text-base-content/60">
              {t("admin.dashboard.no_activity")}
            </div>
          ) : (
            <div class="max-h-[520px] overflow-auto rounded-box border ">
              <table class="table table-zebra table-pin-rows table-sm min-w-[720px]">
                <thead>
                  <tr>
                    <th class="text-left text-accent">{periodHeader.value}</th>
                    {props.series.map((serie) => (
                      <th key={serie.key} class="text-right">
                        <span class="inline-flex items-center gap-2">
                          <span
                            class="inline-flex h-2.5 w-2.5 rounded-full"
                            style={{ background: serie.color }}
                          />
                          {serie.name}
                        </span>
                      </th>
                    ))}
                    <th class="text-right text-accent">
                      {t("admin.dashboard.total")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {periodLabels.value.map((label, index) => (
                    <tr key={label}>
                      <td class="whitespace-nowrap font-medium text-accent">
                        {label}
                      </td>
                      {props.series.map((serie) => (
                        <td key={`${label}-${serie.key}`} class="text-right">
                          {money(serie.data[index] ?? 0)}
                        </td>
                      ))}
                      <td class="text-right font-semibold text-accent">
                        {money(props.totals[index] ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  },
});

export default GroupRevenueBreakdownTable;
